import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import baseUrl from '../config/baseURL';
import { getAuthToken } from '../services/auth';
import { getCurentOrgDataFromStorage } from '../services/organization';
import { Organization } from '../utils/types';
import { META_APP_ID } from '../config/baseURL';

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: any;
  }
}

const WHATSAPP_META_APP_ID =
  process.env.REACT_APP_WHATSAPP_META_APP_ID || META_APP_ID || '590922207308459';

function FacebookOnboarding() {
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [orgError, setOrgError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<Organization>();
  const [isLaunching, setIsLaunching] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const signupPayloadRef = useRef<{ phone_number_id?: string; waba_id?: string } | null>(null);

  const MAX_RETRIES = 15;
  const RETRY_DELAY_MS = 2000;

  useEffect(() => {
    setLoading(true);
    let retryCount = 0;
    let isCancelled = false;

    const fetchWithRetry = async () => {
      while (retryCount < MAX_RETRIES) {
        const response = await getCurentOrgDataFromStorage();

        if (isCancelled) return;

        if (
          response.success &&
          response.data &&
          typeof response.data === 'object' &&
          response.data !== null
        ) {
          setOrg(response.data);
          setOrgError(false);
          setLoading(false);
          return;
        }

        retryCount++;
        setOrgError(true);
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
      }

      setOrgError(true);
      setLoading(false);
    };

    fetchWithRetry();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: WHATSAPP_META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v24.0',
      });
      setSdkReady(true);
    };

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            const { phone_number_id, waba_id } = data.data;
            signupPayloadRef.current = { phone_number_id, waba_id };
            toast({
              title: 'Meta signup completed',
              description: `Phone ID ${phone_number_id} linked to business account ${waba_id}.`,
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          } else if (data.event === 'CANCEL') {
            toast({
              title: 'Setup cancelled',
              description: `You stopped the flow at ${data.data.current_step}.`,
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          } else if (data.event === 'ERROR') {
            toast({
              title: 'Setup error',
              description: data.data.error_message,
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        }
        setSessionInfo(data);
      } catch {
        console.log('Non-JSON response from FB', event.data);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [toast]);

  const fbLoginCallback = async (response: any) => {
    if (!response?.authResponse) {
      setIsLaunching(false);
      return;
    }

    const code = response.authResponse.code;
    const { phone_number_id, waba_id } =
      signupPayloadRef.current || sessionInfo?.data || {};
    const org_id = org?.id;

    if (!phone_number_id || !waba_id) {
      toast({
        title: 'Meta signup details are incomplete',
        description:
          'Finish the embedded signup flow before saving the WhatsApp account.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsLaunching(false);
      return;
    }

    try {
      const token = await getAuthToken();
      if (!token?.success || !token.data) {
        throw new Error('Authentication token is unavailable.');
      }

      const res = await fetch(`${baseUrl}/core/whatsapp/verification/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token.data}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          org_id,
          code,
          phone_number_id,
          waba_id,
        }),
      });

      const data = await res.json();

      if (res.ok && (data.success || data.id || data.phone_number_id)) {
        toast({
          title: 'WhatsApp connected',
          description: 'The number is now ready to use inside your inbox.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        if (org_id) {
          navigate(`/organization/${org_id}/inbox/whatsapp/conversations`);
        }
      } else {
        throw new Error(data.message || 'Verification failed');
      }
    } catch (err: any) {
      toast({
        title: 'Verification error',
        description: err.message || 'An unexpected error occurred.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      toast({
        title: 'Meta SDK not ready',
        description: 'Wait a moment, then try the connection flow again.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLaunching(true);
    window.FB.login(
      (response: any) => {
        fbLoginCallback(response);
      },
      {
        config_id: process.env.REACT_APP_WHATSAPP_CONFIG_ID || '1123409096393175',
        response_type: 'code',
        override_default_response_type: true,
        extras: { version: 'v3' },
      },
    );
  };

  if (loading) {
    return (
      <Box px={{ base: 4, md: 5 }} py={{ base: 6, md: 7 }}>
        <VStack
          spacing={4}
          align="center"
          justify="center"
          minH="260px"
          borderRadius="24px"
          border="1px dashed"
          borderColor="blackAlpha.100"
          bg="gray.50"
        >
          <Spinner size="lg" color="#166534" thickness="4px" />
          <Text fontSize="sm" color="gray.600">
            Loading organization details
          </Text>
        </VStack>
      </Box>
    );
  }

  if (!org?.id) {
    return (
      <Box px={{ base: 4, md: 5 }} py={{ base: 6, md: 7 }}>
        <VStack align="stretch" spacing={4}>
          <Alert status="warning" borderRadius="18px" bg="orange.50" border="1px solid" borderColor="orange.100">
            <AlertIcon />
            Select an organization first before connecting WhatsApp.
          </Alert>
          <Button
            alignSelf="flex-start"
            variant="outline"
            borderRadius="14px"
            onClick={() => navigate('/home')}
          >
            Go to home
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box px={{ base: 1, md: 2 }} py={{ base: 2, md: 3 }}>
      <VStack align="stretch" spacing={4}>
        {orgError && (
          <Alert status="error" borderRadius="16px" bg="red.50" border="1px solid" borderColor="red.100">
            <AlertIcon />
            Failed to load organization data. Refresh and try again.
          </Alert>
        )}

        <Button
          colorScheme="whatsapp"
          bg="#166534"
          _hover={{ bg: '#14532d' }}
          _active={{ bg: '#14532d' }}
          color="white"
          borderRadius="18px"
          h="56px"
          fontWeight="700"
          onClick={launchWhatsAppSignup}
          isLoading={isLaunching}
          isDisabled={isLaunching}
        >
          Connect WhatsApp via Facebook
        </Button>

        <Text fontSize="sm" color="gray.500" lineHeight="1.7">
          This opens the embedded Meta signup flow for <strong>{org.name}</strong>. Keep popups enabled in your browser.
        </Text>

        {!sdkReady && (
          <Text fontSize="sm" color="gray.500">
            Loading Meta setup tools. If the button does not open the popup immediately, wait a moment and try again.
          </Text>
        )}
      </VStack>
    </Box>
  );
}

export default FacebookOnboarding;
